import type {
  IApiError,
  IAPIErrorDetails,
  IAPIErrorDetailsMap,
  IAPIErrorDetailsMapWithLanguage,
  IAPIErrorNestedDetailsMap,
  IProblemDetails,
  ISimpleAxiosError,
} from '@manager/api/types/api-error-types';
import { i18n } from '@manager/plugins/i18n-plugin';
import {
  extractIndices,
  extractNumber,
} from '@manager/utils/build-string-utils';
import type { AxiosError, AxiosResponse } from 'axios';
import {
  escapeRegExp,
  isArray,
  isString,
  snakeCase,
  toNumber,
} from 'lodash-es';
import type { TranslateResult } from 'vue-i18n';

export function isAPIError(input: unknown): input is AxiosError<IApiError> {
  if (!input) {
    return false;
  }

  const axiosResponse = input as AxiosError<IApiError>;
  return axiosResponse.response?.data?.error?.details != null;
}

export function getApiErrorMessages(error: unknown) {
  if (error) {
    if (isAPIError(error)) {
      return error.response?.data.error.details;
    } else if (error instanceof Error) {
      return [error.message];
    }

    return ['validation.unknown_error'];
  }

  return undefined;
}

export function getTranslatedApiErrorMessages(
  error: unknown
): TranslateResult[] {
  const errors = getApiErrorMessages(error);

  if (errors && errors.length) {
    return errors.map((e) => {
      if (isString(e)) {
        return i18n.global.t(e);
      } else {
        return i18n.global.t(e.code, { field: e.field });
      }
    });
  }

  return ['validation.unknown_error'] as TranslateResult[];
}

export function getApiErrorArray(
  error: ISimpleAxiosError<IApiError>
): IAPIErrorDetailsMap {
  const errorDetails = error.response?.data?.error?.details;
  const dummy: IAPIErrorDetailsMap = {};

  errorDetails?.forEach((detail) => {
    dummy[extractNumber(detail.field)] = { ...detail };
  });

  return dummy;
}

/**
 * Returns a map of error details grouped by index and language
 * Example field: 'data.0.name.SV' or 'data.1.name.RU'
 */
export function getApiErrorLanguageArray(
  error: ISimpleAxiosError<IApiError>
): IAPIErrorDetailsMapWithLanguage {
  const errorDetails = error.response?.data?.error?.details;
  const result: IAPIErrorDetailsMapWithLanguage = {};

  errorDetails?.forEach((detail) => {
    const field = detail.field;
    const matches = field.match(/^data\.(\d+)\..*?\.([A-Z]{2})$/);

    if (matches) {
      const [, indexStr, language] = matches;
      const index = Number(indexStr);

      if (!result[index]) {
        result[index] = {};
      }
      if (!result[index][language]) {
        result[index][language] = {
          code: detail.code,
          message: detail.message,
          field: detail.field,
        };
      }
    }
  });

  return result;
}

/**
 * Extracts the error code without the item index
 * Example input error code: '0.0.item_distinct'
 * Returns: '0.item_distinct'
 */
export function extractErrorField(str: string) {
  const regex = /^\d+\.(.*)$/;
  const match = str.match(regex);
  return match ? match[1] : '';
}

/**
 * Returns a map of error details grouped by item and field
 * Use this if the error details are nested, and you need to access them by item and field
 * Example error code: '0.0.item_distinct'
 */
export function getNestedApiErrorMap(
  error: ISimpleAxiosError<IApiError>
): IAPIErrorNestedDetailsMap {
  const errorDetails = error.response?.data?.error?.details;
  const errorMap: IAPIErrorNestedDetailsMap = {};

  errorDetails?.forEach((detail) => {
    const [itemIndex, fieldIndex] = extractIndices(detail.field);

    if (!errorMap[itemIndex]) errorMap[itemIndex] = {};
    errorMap[itemIndex][fieldIndex] = {
      ...detail,
      field: extractErrorField(detail.field),
    };
  });

  return errorMap;
}

/**
 * Throws an error if the given value is null or undefined, returns the value otherwise
 */
export function getValueOrThrow<T>(
  value: T | undefined,
  propName?: string
): NonNullable<T> {
  if (value != null) {
    return value as NonNullable<T>;
  }

  throw new Error(`${propName || 'value'} is undefined or null`);
}

export function getContainingValueOrThrow<T>(
  value: T | undefined,
  containedIn: unknown[],
  propName?: string
): T {
  const checkedValue = getValueOrThrow(value, propName);

  if (containedIn.includes(checkedValue)) {
    return checkedValue;
  }

  throw new Error(
    `${propName || 'value'} is not included in given values: ${JSON.stringify(containedIn)}`
  );
}

export function getArrayOrThrow<T>(value: T | undefined, propName?: string): T {
  const nonNullValue = getValueOrThrow(value, propName);

  if (!isArray(nonNullValue)) {
    throw new Error(`${propName || 'value'} is not an array`);
  }

  return nonNullValue;
}

export function getNonEmptyArrayOrThrow<T>(
  value: T | undefined,
  propName?: string
): T {
  const nonNullValue = getArrayOrThrow(value, propName);

  if (isArray(nonNullValue) && nonNullValue.length <= 0) {
    throw new Error(`${propName || 'value'} is empty`);
  }

  return nonNullValue;
}

/**
 * Throws an error if the given value is null, undefined or not a number, returns a number otherwise
 */
export function getNumberOrThrow<T>(
  value: T | undefined,
  propName?: string
): number {
  const val = getValueOrThrow(value, propName);
  // TODO: add correct formatting i18n
  const numValue = toNumber(`${val}`.replace(',', '.'));

  if (isNaN(numValue) || `${value}` === '') {
    throw new Error(`${propName || 'value'} is not a number: ${value}`);
  }

  return numValue;
}

export function getNumberOrFallback<T, TFallback>(
  value: T | undefined | null,
  fallback: TFallback
): number | TFallback {
  try {
    return getNumberOrThrow(value);
  } catch {
    return fallback;
  }
}

export function getStringOrThrow<T extends string>(
  value: T | undefined | null,
  propName?: string
): T {
  const val = getValueOrThrow(value, propName);

  if (!isString(val)) {
    throw new Error(`${propName || 'value'} is not a string: ${value}`);
  }

  return val;
}

export function getMinNumberOrThrow<T>(
  value: T | undefined,
  min: number,
  propName?: string
): number {
  const val = getNumberOrThrow(value, propName);
  if (val < min) {
    throw new Error(`${propName || 'value'} is lower than ${min}: ${val}`);
  }

  return val;
}

/**
 * It validates if the given data is an API error.
 */
export function isApiError(e: unknown): e is IApiError {
  const error = e as IApiError;

  return (
    error?.error?.code != null &&
    error?.error?.message != null &&
    error?.error?.details != null
  );
}

/**
 * It validates if the given data is an RFC 9457 problem document (billing-api).
 */
export function isProblemDetails(e: unknown): e is IProblemDetails {
  const problem = e as IProblemDetails;

  return (
    isString(problem?.title) &&
    isString(problem?.detail) &&
    typeof problem?.status === 'number'
  );
}

const domainCodeStatuses = [404, 409];

// A refused 422 names its domain code in `detail`; an invalid one lists `errors` instead.
function hasDomainCode(problem: IProblemDetails): boolean {
  if (problem.status === 422) {
    return !problem.errors;
  }

  return domainCodeStatuses.includes(problem.status);
}

/**
 * Folds billing-api's problem document into manager-api's error envelope, so every consumer of
 * `getApiError`, `useApiErrorParser` and `showErrorResponse` reads one shape. 422 field codes
 * become details; 404, 409 and a refused 422 carry their domain i18n key in `code`, also next to
 * classic details.
 */
export function problemDetailsToApiError(problem: IProblemDetails): IApiError {
  if (problem.error) {
    if (hasDomainCode(problem)) {
      return { error: { ...problem.error, code: problem.detail } };
    }

    return { error: problem.error };
  }

  const details = Object.entries(problem.errors ?? {}).flatMap(
    ([field, codes]) => codes.map((code) => ({ code, message: code, field }))
  );

  return {
    error: {
      code: problem.detail || problem.title,
      message: problem.detail,
      details,
    },
  };
}

/**
 * Type guard that checks if the response from the backend is an API error.
 * That means it checks weather the response has the correct structure.
 */
export function isApiResponseError(
  e: unknown
): e is { response: AxiosResponse<IApiError | IProblemDetails> } {
  const data = (e as AxiosError)?.response?.data;
  return isApiError(data) || isProblemDetails(data);
}

export function getApiError(
  error: Error | IApiError | IProblemDetails | undefined | unknown
): IApiError | undefined {
  return toApiError(error) ?? toApiError((error as AxiosError)?.response?.data);
}

// A problem document goes first: billing-api's can carry a classic envelope too.
function toApiError(data: unknown): IApiError | undefined {
  if (isProblemDetails(data)) {
    return problemDetailsToApiError(data);
  }

  if (isApiError(data)) {
    return data;
  }

  return undefined;
}

// `te()` misses a key that stays flat because a message sits on its prefix (`validation.min.numeric`).
function hasMessage(key: string): boolean {
  return i18n.global.t(key) !== key;
}

/**
 * APIs answer with either an i18n key (`validation.*`, `domain.*`) or a bare code. Bare codes are
 * looked up under `apiErrors.` so both resolve without the caller knowing which it got.
 */
export function translateApiErrorCode(
  code: string,
  params?: Record<string, string>
): string | undefined {
  const key = findMessageKey(code);

  return key ? (i18n.global.t(key, params ?? {}) as string) : undefined;
}

function findMessageKey(code: string): string | undefined {
  const namespaced = `apiErrors.${code}`;

  if (hasMessage(namespaced)) {
    return namespaced;
  }

  if (hasMessage(code)) {
    return code;
  }

  // Billing-api appends dynamic data to a domain code after a colon (`domain.x.y:{id}`).
  const separator = code.indexOf(':');

  return separator > 0 ? findMessageKey(code.slice(0, separator)) : undefined;
}

/** True when the message names a placeholder `params` leaves out, e.g. `{min}` of `validation.min`. */
function lacksParams(key: string, params: Record<string, string>): boolean {
  const message: unknown = i18n.global.tm(key);

  if (!isString(message)) {
    return false;
  }

  return [...message.matchAll(/\{(\w+)\}/g)].some(
    ([, name]) => !(name in params)
  );
}

/**
 * Laravel bakes the field path into its validation messages ("The document type field is
 * required."), so the local label has to replace the path, its trailing segment and the
 * humanized spelling of either alike.
 *
 * Whole words only: a trailing segment is often a two-letter language code, which would otherwise
 * match inside an unrelated word and rewrite the middle of the message. A per-item index is
 * dropped for the same reason — `locationIds.0` names the control `locationIds`, and a bare `0`
 * matches any number the message happens to quote.
 */
function substituteFieldLabel(
  message: string,
  field: string,
  label: string
): string {
  // The reported path still leads, so a message quoting it whole is matched as reported.
  const collectionPath = field.replace(/\.\d+$/, '');
  const segments = collectionPath.split('.');
  const lastSegment = segments[segments.length - 1];
  const prefix = segments.slice(0, -1).join('.');
  const humanized = snakeCase(lastSegment).replace(/_/g, ' ');

  const needles = [
    field,
    collectionPath,
    prefix ? `${prefix}.${humanized}` : humanized,
    lastSegment,
    humanized,
  ].filter(Boolean);

  for (const needle of needles) {
    const pattern = new RegExp(`\\b${escapeRegExp(needle)}\\b`, 'i');

    if (pattern.test(message)) {
      return message.replace(pattern, label);
    }
  }

  return message;
}

/**
 * One field detail, translated for the control that owns it. The code resolves via
 * `apiErrors.<code>` or as a bare i18n key with `label` as `{field}`, unless the message needs
 * other placeholders; otherwise the API message is reused with the reported path swapped for `label`.
 */
export function translateApiFieldError(
  detail: IAPIErrorDetails,
  label: string
): string {
  const params = { field: label };
  const key = findMessageKey(detail.code);

  if (key && !lacksParams(key, params)) {
    return i18n.global.t(key, params) as string;
  }

  const substituted = detail.message
    ? substituteFieldLabel(detail.message, detail.field, label)
    : '';

  return (
    substituted ||
    (i18n.global.t('validation.invalid', { field: label }) as string)
  );
}

export function isMigrationBlockedError(error: unknown): boolean {
  return (
    getApiError(error)?.error?.code === 'general.tenant.blocked.for.migration'
  );
}
